import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import { STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { STATSIG_EXPERIMENTS, useStatsigExperiment } from '@src/experiments/statsig-integration'
import { useStatsigDynamicConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-config.hook'
import { DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS } from '@src/quince-components/src/atoms/eddMessageContainer'
import { getBaseMessagingConditions } from '@src/quince-components/src/atoms/productCTA/productCTA'
import { CTA_TYPE, LOW_INVENTORY_THRESHOLD } from '@src/quince-components/src/atoms/productCTA/productCTAConstants'
import { useGoldBarProductEligibility } from '@src/quince-components/src/atoms/productCTA/useGoldBarProductEligibility'
import { getIsGiftCard } from '@src/quince-components/src/atoms/productCTA/utils'
import { VariantPurchaseInfo } from '@src/quince-components/src/atoms/productForm/ProductForm'
import { PURCHASE_TYPES, PurchaseType } from '@src/quince-components/src/atoms/purchaseTypeSelector'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { HandleShopInAppParams } from '@src/quince-site-common/src/hooks/appQrModal/hooks/useShopInAppHandler'
import { useResponsiveAction } from '@src/quince-site-common/src/hooks/use-responsive-action'
import { useWaitlistSmsModal } from '@src/quince-site-common/src/hooks/use-waitlist-sms-modal'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { validateEmail } from '@src/quince-site-integrated/src/utils'
import { scrollToMiniPDPCollectionSection } from '@src/quince-site-pdp/src/organisms/productPageSections/miniPDPCollection/utils'

export interface UseMiniPDPProductCTAParams {
  product: Product
  selectedOptions: ProductVariantOption[]
  productCTAType: CTA_TYPE
  selectedVariantPurchaseInfo: VariantPurchaseInfo | undefined
  areAllVariantOOS: boolean
  inputOptionError?: string
  productFormErrorMessage?: string
  onJoinWaitList: (email?: string) => void
  onAddToCart: (isPreorder?: boolean, selectedPlanId?: number) => Promise<void>
  showPurchaseTypeSelector?: boolean
  purchaseOption?: PurchaseType
  selectedPlanId?: number
  handleShopInApp: ({ product, selectedOptions, isButtonClick }: HandleShopInAppParams) => void
  isCurrentProductOptionLeavingSoon?: boolean
  onClickedShopSimilar?: () => void
}

export const useMiniPDPProductCTA = ({
  product,
  selectedOptions,
  productCTAType,
  selectedVariantPurchaseInfo,
  areAllVariantOOS,
  onJoinWaitList,
  onAddToCart,
  showPurchaseTypeSelector,
  purchaseOption,
  selectedPlanId,
  handleShopInApp,
  isCurrentProductOptionLeavingSoon,
  onClickedShopSimilar,
}: UseMiniPDPProductCTAParams) => {
  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const { isBisWaitlistSmsEnabled, showSmsModal, setShowSmsModal, openSmsModalIfAllowed, onSubmitPhone } =
    useWaitlistSmsModal({ product, selectedOptions })
  const { t } = useTranslation('common')
  const EMAIL_ERROR_MSG = t('please_enter_valid_email_address')

  const eddExp = useStatsigExperiment(STATSIG_EXPERIMENTS.EDD)
  const disableForceEDD = useStatsigDynamicConfig<boolean>(DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS)
  const isEDDExpEnabled =
    // force edd for age restricted products as per existing logic
    eddExp.experimentBucket !== STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.EDD].buckets?.control ||
    (product && productHelper.getIsAgeRestrictedProduct(product) && !disableForceEDD) // force edd for age restricted products

  const { isGoldBarError } = useGoldBarProductEligibility(product, setErrorMessage)

  const selectedVariantAvailableInventory = selectedVariantPurchaseInfo?.availableInventory ?? 0
  const showVariantAvailableInventory =
    ![CTA_TYPE.WAITLIST, CTA_TYPE.PREORDER].includes(productCTAType) &&
    selectedVariantAvailableInventory > 0 &&
    selectedVariantAvailableInventory < LOW_INVENTORY_THRESHOLD

  const baseMessagingConditions = getBaseMessagingConditions(
    selectedVariantPurchaseInfo,
    showVariantAvailableInventory,
    product,
    areAllVariantOOS,
    isGoldBarError,
    eddExp.init,
  )

  const showEDDMsg = productHelper.getIsAgeRestrictedProduct(product)
    ? productCTAType === CTA_TYPE.ADD_TO_CART
    : !(
        productCTAType === CTA_TYPE.WAITLIST ||
        // suggestion is wrong by eslint
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        selectedVariantPurchaseInfo?.isPreorder ||
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        selectedVariantPurchaseInfo?.showMadeToOrder ||
        getIsGiftCard(product?.id) ||
        product?.isMadeToOrder
      )

  const showInStockAndReadyToShip = baseMessagingConditions && !isEDDExpEnabled

  // Instant loading feedback for INP: the spinner paints within the click, then the heavy
  // onAddToCart runs after a yield (see useResponsiveAction). No externalPending source exists
  // in this hook's params, so isPending reflects only local in-flight state.
  const { isPending, run: runAddToCart } = useResponsiveAction(onAddToCart)

  const handleAddToCart = (isPreorder = false, selectedPlanId: number | undefined = undefined) => {
    runAddToCart(isPreorder, selectedPlanId)
  }

  const revertEmailErrorMsg = () => {
    if (errorMessage === EMAIL_ERROR_MSG) {
      setErrorMessage('')
    }
  }

  useEffect(() => {
    if (productCTAType !== CTA_TYPE.WAITLIST) {
      revertEmailErrorMsg()
    }
  }, [productCTAType])

  const handleClick = () => {
    switch (productCTAType) {
      case CTA_TYPE.PREORDER:
        handleAddToCart(true)
        break

      case CTA_TYPE.WAITLIST:
        if (isCurrentProductOptionLeavingSoon) {
          onClickedShopSimilar?.()
          scrollToMiniPDPCollectionSection()
          return
        }
        if (selectedVariantPurchaseInfo?.hasJoinedWaitList) {
          return
        }
        if (AuthUtil.isLoggedIn()) {
          const cookieEmail =
            getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) ??
            getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL) ??
            ''
          onJoinWaitList?.(cookieEmail)
          openSmsModalIfAllowed(cookieEmail)
        } else {
          if (validateEmail(email)) {
            onJoinWaitList?.(email)
            setErrorMessage('')
            setEmail('')
            openSmsModalIfAllowed(email)
          } else {
            setErrorMessage(EMAIL_ERROR_MSG)
          }
        }
        break

      case CTA_TYPE.ADD_TO_CART:
        handleAddToCart(
          false,
          showPurchaseTypeSelector && purchaseOption === PURCHASE_TYPES.SUBSCRIBE && selectedPlanId
            ? selectedPlanId
            : undefined,
        )
        break

      case CTA_TYPE.SHOP_IN_THE_APP:
        handleShopInApp?.({ product: product, selectedOptions: selectedOptions ?? [], isButtonClick: true })
        break

      default:
        captureException(new Error('CTA type not found'), {
          module: MODULES.PRODUCT,
          feature: FEATURES.ADD_TO_CART,
          extraInfo: {
            productId: product.id,
          },
        })
    }
  }

  return {
    setEmail,
    showInStockAndReadyToShip,
    errorMessage,
    handleClick,
    showEDDMsg,
    isGoldBarError,
    isPending,
    isBisWaitlistSmsEnabled,
    showSmsModal,
    setShowSmsModal,
    onSubmitPhone,
  }
}
